import { useState, type ReactNode, type RefObject } from 'react'
import { Link } from 'react-router'
import { egressInGroup, type Host, type MemberRole, type MemberState } from '../../api'
import { t, type Key } from '../../i18n'
import { Button, Chip, Field, Icon, Input, Note, OptionList, Segmented, Tag, type Option } from '../../ui'
import { nodePath } from './api'
import { GroupDialog } from './GroupDialog'
import { isDanger, useDryRun } from './groupAdmin'
import {
  actionRole, drainable, groupErrorText, groupsApi, hasRole, isDown, memberPath, pct, roleState, roleTag, returnable, rolesOf, stateLook,
  type GroupMemberView, type GroupView,
} from './groups'
import type { EgressAddress, GroupRouteRef } from './groupTypes'
import { one, tn } from './nodeSwitch'
import { WarningNotes } from './Warnings'

/*
 * GEG-UI stage 1: the group's own outputs nodes in the console (node-group.md §5.5.11), on the
 * GEG-1a API. Stage 0 and the designer's answer: design/redesign/AUDIT.md §30 GEU-01…GEU-14,
 * claude/geg-ui-stage0-design-answer.md.
 * - Roles (GEU-01 C): an option list of the three valid combinations with what each means; the
 *   group's last member that takes encoders cannot become outputs only (.why).
 * - States per role (GEU-03 A): a role tag + its chip, one line per role, only when a member of the
 *   group has the Outputs role; DOWN is one chip for the member.
 * - Drain (GEU-05 B): one Drain node button; the dialog chooses Fully (default) · Ingest only ·
 *   Outputs only and says what moves; warn note + danger only when outputs move (NG-18).
 * - Group outputs (GEU-06 A): a panel under Members; in mode center one line, no table; a cap held
 *   is an icon and a caption, never colour alone.
 * - Outputs weight (GEU-07): a share of the group's routes, % (owner 03.11).
 * - Egress mode (GEU-08 B): an option list in the group settings.
 * - Outputs name (GEU-09 A + Copy addresses): from GEG-2 — shown when the API carries it.
 * DS: GEU-01…GEU-14 — the words and the rule "a choice with consequences is an option list" come
 * into the system with the DREQ-01 import; no class or token of our own.
 */

/* ── roles ───────────────────────────────────────────────────────────────── */

export type RoleChoice = 'ingest' | 'both' | 'egress'
export const ROLES_OF: Record<RoleChoice, MemberRole[]> = { ingest: ['ingest'], both: ['ingest', 'egress'], egress: ['egress'] }
export const choiceOf = (roles: MemberRole[]): RoleChoice => (roles.includes('ingest') ? (roles.includes('egress') ? 'both' : 'ingest') : 'egress')

const ROLE_LABEL: Record<RoleChoice, Key> = { ingest: 'geg.roles.ingest', both: 'geg.roles.both', egress: 'geg.roles.egress' }
const ROLE_DESC: Record<RoleChoice, Key> = { ingest: 'geg.roles.ingestD', both: 'geg.roles.bothD', egress: 'geg.roles.egressD' }

/** GEU-01 C: the three valid combinations; outputs only is off for the group's last member that takes encoders. */
export function RoleOptions({ value, onChange, lastIngest, group, testId = 'roles' }: { value: RoleChoice; onChange: (v: RoleChoice) => void; lastIngest?: boolean; group: string; testId?: string }) {
  const opts: Option<RoleChoice>[] = (['ingest', 'both', 'egress'] as RoleChoice[]).map((c) => ({
    value: c,
    label: t(ROLE_LABEL[c]),
    ...(c === 'egress' && lastIngest ? { why: tn('geg.why.lastIngest', { group }) } : { desc: t(ROLE_DESC[c]) }),
    testId: `${testId}-${c}`,
  }))
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <span className="sx-label" id={`${testId}-label`}>{t('geg.roles.label')}</span>
      <OptionList value={value} options={opts} onChange={onChange} labelledBy={`${testId}-label`} />
    </div>
  )
}

/** Mode center: a member with the Outputs role waits (GEU-01). A note without a state. */
export const CenterModeNote = ({ group, name }: { group: string; name: string }) => (
  <Note data-testid="roles-center-note">{tn('geg.roles.centerNote', { group, name })}</Note>
)

/** The group's last member that takes encoders (outputs only would leave the group without ingest). */
export const isLastIngest = (m: GroupMemberView, members: GroupMemberView[]) =>
  hasRole(m, 'ingest') && members.filter((x) => hasRole(x, 'ingest')).length === 1

const inGroupMode = (v: GroupView) => (v.egress ? v.egress.mode === 'group' : egressInGroup(v.group.params))

export function ChangeRolesDialog({ m, view, dry, trigger, onClose, onDone }: { m: GroupMemberView; view: GroupView; dry: boolean; trigger: RefObject<HTMLElement | null>; onClose: () => void; onDone: (text: string) => Promise<void> }) {
  const g = view.group
  const [choice, setChoice] = useState<RoleChoice>(choiceOf(rolesOf(m)))
  const roles = ROLES_OF[choice]
  const changed = choice !== choiceOf(rolesOf(m))
  const dr = useDryRun(changed ? { method: 'PATCH', path: memberPath(m.node), body: { member_roles: roles } } : null, dry)
  return (
    <GroupDialog
      testId="member-roles"
      title={tn('geg.roles.title', { name: m.name })}
      action={t('geg.roles.save')}
      ready={changed && !dr.pending}
      danger={isDanger(dr.warnings)}
      returnFocus={trigger}
      onClose={onClose}
      errorText={(e) => groupErrorText(e, { name: m.name })}
      submit={async (totp) => {
        await groupsApi.member(m.node, { member_roles: roles, totp })
        await onDone(t('geg.roles.done', { name: m.name }))
      }}
    >
      <WarningNotes warnings={dr.warnings} ctx={{ name: m.name, group: g.name }} testId="member-roles-warnings" />
      <RoleOptions value={choice} onChange={setChoice} lastIngest={isLastIngest(m, view.members)} group={g.name} testId="member-roles-opt" />
      {roles.includes('egress') && !inGroupMode(view) && <CenterModeNote group={g.name} name={m.name} />}
    </GroupDialog>
  )
}

/* ── states per role (GEU-03 A) ──────────────────────────────────────────── */

/** Show the role tags: only when a member of the group has the Outputs role (else INGEST on every row is noise). */
export const showRoleTags = (members: GroupMemberView[]) => members.some((m) => hasRole(m, 'egress'))

export function RoleStates({ m, tags, hint }: { m: GroupMemberView; tags: boolean; hint?: string }) {
  if (isDown(m)) {
    const was = rolesOf(m).map((r) => (tags ? `${roleTag(r)} ${stateLook(roleState(m, r)!).word}` : stateLook(roleState(m, r)!).word)).join(' · ')
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`member-state-${m.node}`} data-look="down">
        <Chip state="fail" size="sm">{t('group.state.down')}</Chip>
        <span className="sx-cap">{t('group.was', { state: was })}</span>
      </span>
    )
  }
  return (
    <span className="inline-flex flex-col gap-1 py-1" data-testid={`member-state-${m.node}`} data-look={rolesOf(m).map((r) => roleState(m, r)).join('|')} title={hint}>
      {rolesOf(m).map((r) => {
        const st = roleState(m, r)!
        const l = stateLook(st)
        return (
          <span key={r} className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`member-state-${m.node}-${r}`} data-look={st}>
            {tags && <RoleTag r={r} />}
            <Chip state={l.state} size="sm" still>{l.word}</Chip>
            {r === 'ingest' && st === 'joining' && <Tag>{`${Math.max(0, Math.min(10, Math.round(m.ramp * 10)))}/10`}</Tag>}
          </span>
        )
      })}
    </span>
  )
}

/** A role tag with one minimum width, so the chips stand in one column (designer, GEU-03). */
export const RoleTag = ({ r }: { r: MemberRole }) => (
  <Tag className="min-w-[68px] justify-center" data-testid={`role-tag-${r}`}>{roleTag(r)}</Tag>
)

/* ── drain, return, maintenance per role (GEU-05 B) ──────────────────────── */

type DrainChoice = 'all' | 'ingest' | 'egress'

/** Same-node routes of a member (GEG-1b: local) and the others it carries. */
export function routesOf(m: GroupMemberView, routes?: GroupRouteRef[]) {
  const total = m.routes ?? m.hub_routes ?? 0
  const local = routes ? routes.filter((r) => r.local && r.egress === m.node).length : 0
  return { total, local, others: Math.max(0, total - local) }
}

export function DrainDialog({ m, view, host, prov, preset, trigger, onClose, onDone }: {
  m: GroupMemberView
  view: GroupView
  host?: Host
  prov: boolean
  /** "Drain outputs" in Group outputs opens it on Outputs only */
  preset?: DrainChoice
  trigger: RefObject<HTMLElement | null>
  onClose: () => void
  onDone: (text: string) => Promise<void>
}) {
  const g = view.group
  const min = Math.round((g.params?.drain_deadline_s ?? 3600) / 60)
  const both = rolesOf(m).length > 1
  const can = drainable(m)
  const noHost = !prov || !host
  const last = isLastIngest(m, view.members)
  const why = (c: DrainChoice): ReactNode | undefined => {
    const roles: MemberRole[] = c === 'all' ? ['ingest', 'egress'] : [c]
    const st = roles.map((r) => roleState(m, r)).find((s) => s && s !== 'joining' && s !== 'serving')
    if (st) return t('geg.drain.why.state', { state: stateLook(st as MemberState).word })
    if (roles.includes('ingest') && noHost) return t('group.err.noHost', { name: m.name })
    if (c === 'ingest' && last) return tn('geg.why.lastIngest', { group: g.name })
    return undefined
  }
  const choices: DrainChoice[] = both ? ['all', 'ingest', 'egress'] : [rolesOf(m)[0]!]
  const firstOk = (preset && !why(preset) ? preset : undefined) ?? choices.find((c) => !why(c)) ?? choices[0]!
  const [c, setC] = useState<DrainChoice>(firstOk)
  const roles: MemberRole[] = c === 'all' ? rolesOf(m) : [c]
  const r = routesOf(m, view.routes)
  const outputsMove = roles.includes('egress') && r.total > 0
  const otherEgress = view.members.some((x) => x.node !== m.node && hasRole(x, 'egress') && roleState(x, 'egress') === 'serving')
  const facts: ReactNode[] = []
  if (roles.includes('ingest')) facts.push(t('group.drain.noNew'), tn('group.drain.left', { n: m.connections, min }), t('group.drain.apps'))
  if (roles.includes('ingest') && !roles.includes('egress') && hasRole(m, 'egress') && r.total > 0) facts.push(tn(one(r.total, 'geg.drain.keeps.one', 'geg.drain.keeps'), { name: m.name, n: r.total }))
  if (roles.includes('egress')) facts.push(r.total > 0 ? tn(one(r.total, 'geg.drain.sends.one', 'geg.drain.sends'), { n: r.total, l: r.local }) : t('geg.drain.empty'))
  if (outputsMove && !otherEgress) facts.push(t('geg.drain.toCenter'))
  const opts: Option<DrainChoice>[] = choices.map((x) => {
    const w = why(x)
    return { value: x, label: t(`geg.drain.${x}` as Key), ...(w ? { why: w } : { desc: t(`geg.drain.${x}D` as Key, { name: m.name }) }), testId: `member-drain-${x}` }
  })
  return (
    <GroupDialog
      testId="member-drain"
      title={tn('group.confirm.drain', { name: m.name })}
      action={t('group.btn.drain')}
      ready={!why(c) && can.length > 0}
      danger={outputsMove}
      returnFocus={trigger}
      onClose={onClose}
      errorText={(e) => groupErrorText(e, { name: m.name })}
      submit={async (totp) => {
        await groupsApi.action(m.node, 'drain', { role: c === 'all' ? actionRole(m, rolesOf(m)) : c, deadline_s: min * 60, totp })
        await onDone(t(c === 'egress' ? 'geg.done.drainEgress' : c === 'ingest' && both ? 'geg.done.drainIngest' : 'group.done.drain', { name: m.name }))
      }}
    >
      {outputsMove && (
        <Note state="warn" data-testid="member-drain-move">
          <b className="font-medium text-text">{tn(one(r.total, 'geg.drain.warn.one', 'geg.drain.warn'), { n: r.total, min })}</b> {t('geg.drain.warnB')}
        </Note>
      )}
      {both && (
        <div className="flex flex-col gap-1.5">
          <span className="sx-label" id="member-drain-what">{t('geg.drain.what')}</span>
          <OptionList value={c} options={opts} onChange={setC} labelledBy="member-drain-what" />
        </div>
      )}
      {/* NG-18: the warning first, then the choice, then what it does */}
      <ul data-testid="member-drain-facts">
        {facts.map((f, i) => (
          <li key={i}>{f}</li>
        ))}
      </ul>
    </GroupDialog>
  )
}

/** Return the drained roles (the API's return per role; GEU-05: one button returns them all). */
export function ReturnDialog({ m, view, trigger, onClose, onDone }: { m: GroupMemberView; view: GroupView; trigger: RefObject<HTMLElement | null>; onClose: () => void; onDone: (text: string) => Promise<void> }) {
  const p = view.group.params
  const roles = returnable(m)
  const facts: ReactNode[] = []
  if (roles.includes('ingest')) facts.push(p?.auto_ramp === false ? t('group.return.manual') : tn('group.return.ramp', { min: Math.round((p?.ramp_s ?? 600) / 60) }), t('group.return.agent', { name: m.name }))
  if (roles.includes('egress')) facts.push(t('geg.return.egress'))
  return (
    <GroupDialog
      testId="member-return"
      title={tn('group.confirm.return', { name: m.name, group: view.group.name })}
      facts={facts}
      action={t('group.btn.return')}
      returnFocus={trigger}
      onClose={onClose}
      errorText={(e) => groupErrorText(e, { name: m.name })}
      submit={async (totp) => {
        await groupsApi.action(m.node, 'return', { role: actionRole(m, roles), totp })
        await onDone(t('group.done.return', { name: m.name }))
      }}
    />
  )
}

/* ── the egress mode (GEU-08 B) ──────────────────────────────────────────── */

export function EgressModeOptions({ value, onChange, nodes }: { value: 'center' | 'group'; onChange: (v: 'center' | 'group') => void; nodes: number }) {
  const opts: Option<'center' | 'group'>[] = [
    { value: 'center', label: t('geg.mode.center'), desc: t('geg.mode.centerD'), testId: 'group-set-mode-center' },
    {
      value: 'group', label: t('geg.mode.group'), right: tn(one(nodes, 'geg.mode.nodes.one', 'geg.mode.nodes'), { n: nodes }),
      desc: nodes ? t('geg.mode.groupD') : `${t('geg.mode.groupD')} ${t('geg.mode.noNodes')}`, testId: 'group-set-mode-group',
    },
  ]
  return (
    <div className="flex flex-col gap-1.5" data-testid="group-set-mode">
      <span className="sx-label" id="group-set-mode-label">{t('geg.mode.label')}</span>
      <OptionList value={value} options={opts} onChange={onChange} labelledBy="group-set-mode-label" />
    </div>
  )
}

/* ── Group outputs (GEU-06 A) ────────────────────────────────────────────── */

export type OutputsAsk = { kind: 'eweight' | 'edrain'; m: GroupMemberView }

export function GroupOutputsPanel({ view, open }: { view: GroupView; open: (a: OutputsAsk, el: HTMLElement) => void }) {
  const g = view.group
  const egress = view.members.filter((m) => hasRole(m, 'egress')).sort((a, b) => a.name.localeCompare(b.name))
  const group = inGroupMode(view)
  if (!group) {
    if (!egress.length) return null
    // mode center: no table, one line without a state + the way to the settings (designer, GEU-06)
    return (
      <Note data-testid="group-outputs-center">
        {tn('geg.out.center', { group: g.name, names: egress.map((m) => m.name).join(', ') })}{' '}
        <a href="#group-settings" className="text-accent hover:underline" data-testid="group-outputs-settings-link">{t('geg.out.settingsLink')}</a>
      </Note>
    )
  }
  const center = view.egress?.routes_center ?? 0
  const serving = egress.filter((m) => roleState(m, 'egress') === 'serving' && !isDown(m)).length
  return (
    <section className="sx-panel flex flex-col !p-0" aria-labelledby="group-outputs-title" data-testid="group-outputs">
      <div className="sx-toolbar flex-wrap">
        <span className="sx-label" id="group-outputs-title">{t('geg.out.title')}</span>
        <span className="sx-mono text-muted">{egress.length}</span>
        <span style={{ flex: 1 }} />
        <span className="sx-cap">{t('geg.mode.label')}</span>
        <span className="text-[13px] text-text-soft" data-testid="group-outputs-mode">{t('geg.mode.groupShort')}</span>
      </div>
      {center > 0 && (
        <div className="p-3 pb-0">
          <Note state="warn" data-testid="group-outputs-center-warn">
            <b className="font-medium text-text">{tn(one(center, 'geg.warnCenter.one', 'geg.warnCenter'), { n: center, group: g.name })}</b>{' '}
            {t(view.egress?.center_reason === 'none' || serving === 0 ? 'geg.centerNoneB' : 'geg.centerBusyB')}
          </Note>
        </div>
      )}
      {egress.length === 0 ? (
        <p className="m-0 px-3 py-3 text-[13px] text-muted" data-testid="group-outputs-empty">{t('geg.out.empty')}</p>
      ) : (
        <>
          <OutputsTable members={egress} view={view} open={open} />
          <OutputsCards members={egress} view={view} open={open} />
        </>
      )}
      {view.egress?.name && <OutputsName name={view.egress.name} addresses={view.egress.addresses ?? []} />}
    </section>
  )
}

function Caption({ m, view }: { m: GroupMemberView; view: GroupView }) {
  const r = routesOf(m, view.routes)
  return (
    <>
      <span className="sx-cap block whitespace-normal" data-testid={`out-sends-${m.node}`}>
        {r.total ? tn(one(r.total, 'geg.out.sends.one', 'geg.out.sends'), { n: r.total, l: r.local, o: r.others }) : t('geg.out.sendsIdle')}
      </span>
      {m.egress_cap_held && (
        <span className="sx-cap block whitespace-normal" data-testid={`out-capheld-${m.node}`}>{t('geg.out.capHeld')}</span>
      )}
    </>
  )
}

/** The cap: a held cap is the state-warn icon before the number and a caption (rule 3 README: never colour alone). */
function ECap({ m }: { m: GroupMemberView }) {
  if (m.egress_cap_pct === undefined) return <span className="text-muted">—</span>
  return (
    <span className={`inline-flex items-center gap-1 ${m.egress_cap_held ? 'text-warn' : ''}`} data-testid={`out-cap-${m.node}`} data-held={m.egress_cap_held ? 'true' : undefined}>
      {m.egress_cap_held && <Icon name="state-warn" size={12} />}
      {pct(m.egress_cap_pct)}
    </span>
  )
}

function EWeight({ m }: { m: GroupMemberView }) {
  const manual = m.egress_weight_mode === 'manual'
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`out-weight-${m.node}`} data-mode={manual ? 'manual' : 'auto'}>
      <Tag>{t(manual ? 'group.tag.manual' : 'group.tag.auto')}</Tag>
      {manual && <span className="sx-mono text-text-soft">{pct(m.egress_weight_pct)}</span>}
    </span>
  )
}

function EState({ m }: { m: GroupMemberView }) {
  if (isDown(m)) return <Chip state="fail" size="sm">{t('group.state.down')}</Chip>
  const l = stateLook(roleState(m, 'egress') ?? 'serving')
  return <Chip state={l.state} size="sm" still>{l.word}</Chip>
}

function EActions({ m, open }: { m: GroupMemberView; open: (a: OutputsAsk, el: HTMLElement) => void }) {
  const st = roleState(m, 'egress')
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1.5 md:flex-nowrap">
      <Button size="sm" className="max-md:min-h-[44px]" data-testid={`out-weight-btn-${m.node}`} onClick={(e) => open({ kind: 'eweight', m }, e.currentTarget)}>
        {t('group.btn.weight')}
      </Button>
      {(st === 'serving' || st === 'joining') && (
        <Button size="sm" className="max-md:min-h-[44px]" data-testid={`out-drain-btn-${m.node}`} onClick={(e) => open({ kind: 'edrain', m }, e.currentTarget)}>
          {t('geg.btn.drainOutputs')}
        </Button>
      )}
    </span>
  )
}

function OutputsTable({ members, view, open }: { members: GroupMemberView[]; view: GroupView; open: (a: OutputsAsk, el: HTMLElement) => void }) {
  return (
    <div className="hidden overflow-x-auto md:block">
      <table className="sx-table compact" data-testid="group-outputs-table">
        <thead>
          <tr>
            <th scope="col">{t('geg.col.node')}</th>
            <th scope="col">{t('group.col.state')}</th>
            <th scope="col" className="num">{t('geg.col.routes')}</th>
            <th scope="col" className="num">{t('geg.col.local')}</th>
            <th scope="col" className="num">{t('geg.col.others')}</th>
            <th scope="col" className="num">{t('group.col.share')}</th>
            <th scope="col" className="num">{t('group.col.cap')}</th>
            <th scope="col">{t('group.col.weight')}</th>
            <th scope="col" className="num"><span className="sr-only">{t('group.col.actions')}</span></th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const r = routesOf(m, view.routes)
            return (
              <tr key={m.node} data-testid={`out-row-${m.node}`}>
                <td className="!h-auto py-1.5">
                  <Link to={nodePath(m.node)} className="nm hover:underline">
                    <Icon name="node" size={16} className="text-text-soft" />
                    <span className="sx-mono">{m.name}</span>
                  </Link>
                  <Caption m={m} view={view} />
                </td>
                <td><EState m={m} /></td>
                <td className="num tabular" data-testid={`out-routes-${m.node}`}>{r.total}</td>
                <td className="num tabular">{view.routes ? r.local : '—'}</td>
                <td className="num tabular">{view.routes ? r.others : '—'}</td>
                <td className="num tabular" data-testid={`out-share-${m.node}`}>{pct(m.egress_share)}</td>
                <td className="num tabular"><ECap m={m} /></td>
                <td><EWeight m={m} /></td>
                <td className="num tabular"><EActions m={m} open={open} /></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function OutputsCards({ members, view, open }: { members: GroupMemberView[]; view: GroupView; open: (a: OutputsAsk, el: HTMLElement) => void }) {
  return (
    <ul className="flex flex-col md:hidden" data-testid="group-outputs-cards">
      {members.map((m) => {
        const r = routesOf(m, view.routes)
        return (
          <li key={m.node} className="flex flex-col gap-2 border-t border-line px-3 py-3" data-testid={`out-card-${m.node}`}>
            <span className="flex items-center justify-between gap-2">
              <Link to={nodePath(m.node)} className="inline-flex min-h-[44px] items-center gap-2 font-medium">
                <Icon name="node" size={16} className="text-text-soft" />
                <span className="sx-mono">{m.name}</span>
              </Link>
              <EState m={m} />
            </span>
            <Caption m={m} view={view} />
            <span className="grid grid-cols-4 gap-2 text-[12px]">
              <Mini k={t('geg.col.routes')} v={r.total} />
              <Mini k={t('geg.col.local')} v={view.routes ? r.local : '—'} />
              <Mini k={t('group.col.share')} v={pct(m.egress_share)} />
              <Mini k={t('group.col.cap')} v={<ECap m={m} />} />
            </span>
            <span className="flex items-center justify-between gap-2">
              <EWeight m={m} />
            </span>
            <EActions m={m} open={open} />
          </li>
        )
      })}
    </ul>
  )
}

const Mini = ({ k, v }: { k: string; v: ReactNode }) => (
  <span className="flex flex-col gap-0.5">
    <span className="sx-label">{k}</span>
    <span className="sx-mono">{v}</span>
  </span>
)

/* ── the outputs name (GEU-09 A + Copy addresses; GEG-2) ─────────────────── */

function useCopied() {
  const [copied, setCopied] = useState(false)
  const copy = (v: string) => {
    void navigator.clipboard?.writeText(v).catch(() => undefined)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }
  return { copied, copy }
}

function OutputsName({ name, addresses }: { name: string; addresses: EgressAddress[] }) {
  const n = useCopied()
  const a = useCopied()
  const inName = addresses.filter((x) => !x.waiting)
  const waiting = addresses.some((x) => x.waiting)
  return (
    <div className="grid gap-4 border-t border-line p-3 md:grid-cols-[minmax(0,440px)_minmax(0,1fr)]" data-testid="group-outputs-name">
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="sx-label">{t('geg.name.label')}</span>
        <span className="sx-combo" data-testid="group-outputs-name-value">
          <span className="v truncate" title={name}>{name}</span>
          <button type="button" className="copy" aria-label={`${t('group.copy')} ${name}`} onClick={() => n.copy(name)}>
            <Icon name={n.copied ? 'check' : 'copy'} size={16} />
            <span aria-live="polite">{t(n.copied ? 'group.copied' : 'group.copy')}</span>
          </button>
        </span>
        <span className="sx-cap">{t('geg.name.help')}</span>
      </div>
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="flex items-center gap-2">
          <span className="sx-label">{t('geg.name.addrs')} <span className="sx-mono">{inName.length}</span></span>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" className="max-md:min-h-[44px]" data-testid="group-outputs-copy-addrs" onClick={() => a.copy(inName.map((x) => x.addr).join('\n'))}>
            <Icon name={a.copied ? 'check' : 'copy'} size={16} />
            <span aria-live="polite">{t(a.copied ? 'group.copied' : 'geg.name.copyAddrs')}</span>
          </Button>
        </span>
        <table className="sx-table compact" data-testid="group-outputs-addrs">
          <tbody>
            {addresses.map((x) => (
              <tr key={`${x.addr}-${x.node}`} data-testid={`addr-${x.node}`}>
                <td className="sx-mono">{x.addr}</td>
                <td className="sx-mono text-text-soft">{x.name ?? x.node}</td>
                <td>
                  {x.center ? (
                    <Tag title={t('geg.name.backupTitle')}>{t('geg.name.backup')}</Tag>
                  ) : x.waiting ? (
                    <Chip state="idle" size="sm" still>{t('gx.ready.pending')}</Chip>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {waiting && <span className="sx-cap" data-testid="group-outputs-waiting">{t('geg.name.waiting')}</span>}
      </div>
    </div>
  )
}

/* ── the outputs weight (GEU-07: a share of the group's routes) ─────────── */

export function EgressWeightDialog({ m, trigger, onClose, onDone }: { m: GroupMemberView; trigger: RefObject<HTMLElement | null>; onClose: () => void; onDone: (text: string) => Promise<void> }) {
  const [mode, setMode] = useState<'auto' | 'manual'>(m.egress_weight_mode === 'manual' ? 'manual' : 'auto')
  const [share, setShare] = useState(m.egress_weight_pct !== undefined ? String(m.egress_weight_pct) : '')
  const [cap, setCap] = useState(m.egress_cap_pct !== undefined ? String(m.egress_cap_pct) : '')
  const shareN = share === '' ? undefined : Number(share)
  const capN = cap === '' ? undefined : Number(cap)
  const shareOk = mode === 'auto' || (shareN !== undefined && shareN >= 0 && shareN <= 100)
  const capOk = capN === undefined || (capN > 0 && capN <= 100)
  const over = mode === 'manual' && shareN !== undefined && capN !== undefined && shareN > capN
  return (
    <GroupDialog
      testId="out-weight"
      title={tn('geg.weight.title', { name: m.name })}
      action={t('group.btn.saveWeight')}
      ready={shareOk && capOk && !over}
      returnFocus={trigger}
      onClose={onClose}
      errorText={(e) => groupErrorText(e, { name: m.name, share: shareN, cap: capN })}
      submit={async (totp) => {
        await groupsApi.member(m.node, { egress_weight_mode: mode, egress_weight_pct: mode === 'manual' ? (shareN ?? 0) : null, egress_cap_pct: capN ?? null, totp })
        await onDone(t('group.done.weight', { name: m.name }))
      }}
    >
      <div className="sx-field">
        <span className="lbl">{t('group.weight.mode')}</span>
        <Segmented
          label={t('group.weight.mode')}
          value={mode}
          onChange={setMode}
          options={[
            { value: 'auto', label: t('group.weight.auto'), testId: 'out-weight-auto' },
            { value: 'manual', label: t('group.weight.manual'), testId: 'out-weight-manual' },
          ]}
        />
      </div>
      {mode === 'manual' && (
        <Field id="out-weight-share" label={t('geg.weight.share')} help={t('geg.weight.shareHelp')} error={shareOk ? undefined : t('group.err.needShare')}>
          <Input id="out-weight-share" inputMode="decimal" value={share} onChange={(e) => setShare(e.target.value.replace(/[^\d.]/g, ''))} data-testid="out-weight-share" />
        </Field>
      )}
      <Field id="out-weight-cap" label={t('geg.weight.cap')} help={t('group.weight.capHelp')} error={!capOk ? t('group.err.pct') : over ? t('group.err.overCap', { share: shareN!, cap: capN! }) : undefined}>
        <Input id="out-weight-cap" inputMode="decimal" value={cap} onChange={(e) => setCap(e.target.value.replace(/[^\d.]/g, ''))} data-testid="out-weight-cap" />
      </Field>
    </GroupDialog>
  )
}
